import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { trustColor } from "../../utils/format";
import { useIsMobile } from "../../hooks/useIsMobile";
import { KakaoMapView } from "../../components/KakaoMapView";
import type { ListingResponse } from "../listings/types";

interface Broker {
  id: string;
  name: string;
  office_name: string;
  license_number: string;
  phone: string;
  role: string;
  region_sido: string;
  region_sigungu: string;
  trust_score: number;
  total_listings: number;
  total_deals_closed: number;
  nda_violations: number;
  is_verified: boolean;
  is_suspended: boolean;
  created_at: string;
  verification_status: string;
  verification_requested_at: string;
  verification_reject_reason: string;
}

interface Stats {
  brokers: { total: number; verified: number; suspended: number; pending_verification: number };
  listings: { total: number; active: number };
  ndas: { total: number; approved: number };
}

export function AdminPage() {
  const navigate = useNavigate();
  const m = useIsMobile();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tab, setTab] = useState<"overview" | "brokers" | "pending-verify" | "map" | "owner-listings">("overview");
  const [mapListings, setMapListings] = useState<ListingResponse[]>([]);
  const [mapBrokerFilter, setMapBrokerFilter] = useState<string>("");
  const [loadingMapListings, setLoadingMapListings] = useState(false);

  // 땅주인 매물
  const [ownerListings, setOwnerListings] = useState<any[]>([]);
  const [ownerListingsLoading, setOwnerListingsLoading] = useState(false);
  const [ownerStatusFilter, setOwnerStatusFilter] = useState<string>("");

  // 신뢰도 조정 모달
  const [trustModal, setTrustModal] = useState<{ brokerId: string; name: string } | null>(null);
  const [trustChange, setTrustChange] = useState("");
  const [trustReason, setTrustReason] = useState("");

  // 서류 보기 모달
  const [fileModal, setFileModal] = useState<{ brokerId: string; name: string } | null>(null);
  const [fileModalFiles, setFileModalFiles] = useState<string[]>([]);
  const [fileModalLoading, setFileModalLoading] = useState(false);

  // 거절 사유 모달
  const [rejectModal, setRejectModal] = useState<{ brokerId: string; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchData = async () => {
    try {
      const [statsRes, brokersRes] = await Promise.all([
        apiFetch<{ brokers: Stats["brokers"]; listings: Stats["listings"]; ndas: Stats["ndas"] }>("/api/admin/stats"),
        apiFetch<{ brokers: Broker[] }>("/api/admin/brokers"),
      ]);
      setStats(statsRes);
      setBrokers(brokersRes.brokers);
    } catch {
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // 땅주인 매물 탭 진입 시 로드
  useEffect(() => {
    if (tab !== "owner-listings") return;
    setOwnerListingsLoading(true);
    const qs = ownerStatusFilter ? `?status=${ownerStatusFilter}` : "";
    apiFetch<{ listings: any[] }>(`/api/admin/owner-listings${qs}`)
      .then(res => setOwnerListings(res.listings || []))
      .catch(() => setOwnerListings([]))
      .finally(() => setOwnerListingsLoading(false));
  }, [tab, ownerStatusFilter]);

  // 지도 탭 진입 시 전체 매물 로드 (한 번만)
  useEffect(() => {
    if (tab !== "map" || mapListings.length > 0) return;
    setLoadingMapListings(true);
    apiFetch<{ listings: ListingResponse[] }>("/api/listings?limit=1000")
      .then(res => setMapListings(res.listings || []))
      .catch((e) => console.warn('map listings fetch failed:', e))
      .finally(() => setLoadingMapListings(false));
  }, [tab]);

  const handleAction = async (brokerId: string, action: string, body?: object) => {
    setActionLoading(brokerId + action);
    try {
      await apiFetch(`/api/admin/brokers/${brokerId}/${action}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      await fetchData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const loadVerificationFiles = async (brokerId: string, name: string) => {
    setFileModal({ brokerId, name });
    setFileModalLoading(true);
    setFileModalFiles([]);
    try {
      const res = await apiFetch<{ files: string[]; created_at: string | null }>(
        `/api/admin/brokers/${brokerId}/verification-files`
      );
      setFileModalFiles(res.files || []);
    } catch {
      setFileModalFiles([]);
    } finally {
      setFileModalLoading(false);
    }
  };

  const handleRejectWithReason = async () => {
    if (!rejectModal) return;
    await handleAction(rejectModal.brokerId, "reject-verify", { reason: rejectReason });
    setRejectModal(null);
    setRejectReason("");
  };

  const handleTrustSubmit = async () => {
    if (!trustModal || !trustChange || !trustReason) return;
    await handleAction(trustModal.brokerId, "adjust-trust", {
      score_change: Number(trustChange),
      reason: trustReason,
    });
    setTrustModal(null);
    setTrustChange("");
    setTrustReason("");
  };

  if (loading) {
    return <div style={s.loading}>관리자 데이터 불러오는 중...</div>;
  }

  return (
    <div style={s.container}>
      <header style={{ ...s.header, padding: m ? "12px 12px" : "16px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: m ? 8 : 12 }}>
          <h1 style={{ ...s.headerLogo, fontSize: m ? 18 : 22 }}>
            <span style={{ color: "var(--lt-gold)" }}>땅</span>신뢰
          </h1>
          <span style={s.adminBadge}>ADMIN</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Link to="/admin/bulk-upload" style={{ ...s.bulkBtn, padding: m ? "6px 10px" : "8px 16px", fontSize: m ? 12 : 13 }}>
            {m ? "일괄등록" : "엑셀 일괄 등록"}
          </Link>
          <button onClick={() => navigate("/dashboard")} style={{ ...s.backBtn, padding: m ? "6px 10px" : "8px 16px", fontSize: m ? 12 : 13 }}>
            대시보드로
          </button>
        </div>
      </header>

      {/* 탭 */}
      <div style={{ ...s.tabs, padding: m ? "0 12px" : "0 24px" }}>
        <button
          style={{ ...s.tab, ...(tab === "overview" ? s.tabActive : {}) }}
          onClick={() => setTab("overview")}
        >
          전체 현황
        </button>
        <button
          style={{ ...s.tab, ...(tab === "brokers" ? s.tabActive : {}) }}
          onClick={() => setTab("brokers")}
        >
          중개사 관리
        </button>
        <button
          style={{ ...s.tab, ...(tab === "pending-verify" ? s.tabActive : {}), display: "flex", alignItems: "center", gap: 4 }}
          onClick={() => setTab("pending-verify")}
        >
          {"인증 대기"}
          {brokers.filter((b) => b.verification_status === "pending").length > 0 && (
            <span style={{ padding: "1px 6px", borderRadius: 10, background: "var(--lt-orange)", color: "#fff", fontSize: 10, fontWeight: 700 }}>
              {brokers.filter((b) => b.verification_status === "pending").length}
            </span>
          )}
        </button>
        <button
          style={{ ...s.tab, ...(tab === "map" ? s.tabActive : {}) }}
          onClick={() => setTab("map")}
        >
          전체 매물 지도
        </button>
        <button
          style={{ ...s.tab, ...(tab === "owner-listings" ? s.tabActive : {}), display: "flex", alignItems: "center", gap: 4 }}
          onClick={() => setTab("owner-listings")}
        >
          {"🌱 땅주인 등록"}
        </button>
      </div>

      <main style={tab === "map" ? {} : s.main}>
        {tab === "overview" && stats && (
          <>
            <h2 style={s.sectionTitle}>플랫폼 현황</h2>
            <div style={{ ...s.statsGrid, gridTemplateColumns: m ? "repeat(2, 1fr)" : "repeat(4, 1fr)" }}>
              <Stat label="전체 중개사" value={stats.brokers.total} />
              <Stat label="인증 완료" value={stats.brokers.verified} color="var(--lt-green)" />
              <Stat label="인증 대기" value={stats.brokers.pending_verification} color="var(--lt-orange)" />
              <Stat label="정지 계정" value={stats.brokers.suspended} color="var(--lt-red)" />
              <Stat label="전체 매물" value={stats.listings.total} />
              <Stat label="활성 매물" value={stats.listings.active} color="var(--lt-blue)" />
              <Stat label="전체 NDA" value={stats.ndas.total} />
              <Stat label="승인 NDA" value={stats.ndas.approved} color="var(--lt-green)" />
            </div>
          </>
        )}

        {tab === "brokers" && (
          <>
            <h2 style={s.sectionTitle}>중개사 목록 ({brokers.length}명)</h2>
            <div style={s.brokerList}>
              {brokers.map((b) => (
                <div key={b.id} style={s.brokerCard}>
                  <div style={s.brokerTop}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const }}>
                        <strong style={{ fontSize: 16 }}>{b.name}</strong>
                        {b.role === "admin" && <span style={s.roleBadge}>관리자</span>}
                        {b.is_suspended && <span style={s.suspendBadge}>정지</span>}
                        {b.verification_status === "pending" && <span style={{ padding: "1px 6px", borderRadius: 4, background: "rgba(245,158,11,0.15)", color: "var(--lt-orange)", fontSize: 11, fontWeight: 600 }}>{"심사중"}</span>}
                      </div>
                      <p style={s.brokerSub}>{b.office_name}</p>
                      <p style={s.brokerSub}>
                        {b.region_sido} {b.region_sigungu} | {b.license_number}
                      </p>
                    </div>
                    <div style={{ textAlign: "right" as const }}>
                      <div
                        style={{
                          ...s.trustCircle,
                          borderColor: trustColor(b.trust_score),
                          color: trustColor(b.trust_score),
                        }}
                      >
                        {b.trust_score}
                      </div>
                      <p style={{ ...s.brokerSub, marginTop: 4 }}>
                        매물 {b.total_listings} | 거래 {b.total_deals_closed}
                      </p>
                    </div>
                  </div>

                  {/* 액션 버튼 (관리자 자신에게는 표시 안 함) */}
                  {b.role !== "admin" && (
                    <div style={s.actions}>
                      {!b.is_verified ? (
                        <>
                          <ActionBtn
                            label="인증 승인"
                            color="var(--lt-green)"
                            loading={actionLoading === b.id + "verify"}
                            onClick={() => handleAction(b.id, "verify")}
                          />
                          <ActionBtn
                            label="인증 거절"
                            color="var(--lt-red)"
                            loading={actionLoading === b.id + "reject-verify"}
                            onClick={() => setRejectModal({ brokerId: b.id, name: b.name })}
                          />
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: "var(--lt-green)" }}>인증됨</span>
                      )}

                      {!b.is_suspended ? (
                        <ActionBtn
                          label="정지"
                          color="var(--lt-red)"
                          loading={actionLoading === b.id + "suspend"}
                          onClick={() => handleAction(b.id, "suspend", { reason: "관리자 판단" })}
                        />
                      ) : (
                        <ActionBtn
                          label="정지 해제"
                          color="var(--lt-blue)"
                          loading={actionLoading === b.id + "unsuspend"}
                          onClick={() => handleAction(b.id, "unsuspend")}
                        />
                      )}

                      <ActionBtn
                        label="신뢰도 조정"
                        color="var(--lt-gold)"
                        onClick={() => setTrustModal({ brokerId: b.id, name: b.name })}
                      />

                      <ActionBtn
                        label="NDA 위반"
                        color="var(--lt-red)"
                        loading={actionLoading === b.id + "nda-violation"}
                        onClick={() => handleAction(b.id, "nda-violation", { reason: "관리자 판단에 의한 NDA 위반 처리" })}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "map" && (
          <>
            <div style={{ padding: "24px 16px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" as const, gap: 12, marginBottom: 16 }}>
                <h2 style={s.sectionTitle}>전체 매물 지도 ({(mapBrokerFilter
                  ? mapListings.filter(l => l.broker_id === mapBrokerFilter)
                  : mapListings).length}건)</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>브로커 필터:</span>
                  <select
                    value={mapBrokerFilter}
                    onChange={e => setMapBrokerFilter(e.target.value)}
                    style={{
                      padding: "6px 10px", borderRadius: "var(--radius-md)",
                      border: "1px solid var(--border-medium)",
                      background: "var(--bg-secondary)", color: "var(--text-primary)",
                      fontSize: 13, cursor: "pointer",
                    }}
                  >
                    <option value="">전체</option>
                    {brokers.map(b => (
                      <option key={b.id} value={b.id}>{b.name} ({b.office_name})</option>
                    ))}
                  </select>
                  {mapBrokerFilter && (
                    <button
                      onClick={() => setMapBrokerFilter("")}
                      style={{ padding: "6px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "transparent", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer" }}
                    >초기화</button>
                  )}
                </div>
              </div>

              {/* 범례 */}
              <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" as const }}>
                {[
                  { label: "공개중", color: "#FBBF24" },
                  { label: "거래완료", color: "#3B82F6" },
                  { label: "숨김", color: "#6B7280" },
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: item.color }} />
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {loadingMapListings ? (
              <div style={{ textAlign: "center" as const, padding: "60px 0", color: "var(--text-muted)", fontSize: 14 }}>
                매물 데이터 불러오는 중...
              </div>
            ) : (
              <KakaoMapView
                active={true}
                updateKey={mapBrokerFilter || "all"}
                height="calc(100vh - 200px)"
                mapStyle={{ minHeight: 600, width: "100%" }}
                listings={mapBrokerFilter
                  ? mapListings.filter(l => l.broker_id === mapBrokerFilter)
                  : mapListings}
                allListings={mapListings}
                statusColorMode={true}
              />
            )}
          </>
        )}

        {tab === "owner-listings" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" as const, gap: 12, marginBottom: 20 }}>
              <h2 style={s.sectionTitle}>{"🌱"} 땅주인 등록 매물 ({ownerListings.length}건)</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>상태 필터:</span>
                <select
                  value={ownerStatusFilter}
                  onChange={(e) => setOwnerStatusFilter(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 13 }}
                >
                  <option value="">전체</option>
                  <option value="pending">대기중</option>
                  <option value="matched">중개사 연결됨</option>
                  <option value="accepted">수락됨</option>
                  <option value="closed">완료</option>
                </select>
              </div>
            </div>
            {ownerListingsLoading ? (
              <p style={{ color: "var(--text-muted)" }}>불러오는 중...</p>
            ) : ownerListings.length === 0 ? (
              <p style={{ color: "var(--text-muted)" }}>등록된 땅주인 매물이 없습니다</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 12 }}>
                {ownerListings.map((l: any) => (
                  <div key={l.id} style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-light)", borderLeft: "3px solid var(--lt-gold)", borderRadius: "var(--radius-lg)", padding: "16px 20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap" as const, gap: 8 }}>
                      <div>
                        <p style={{ margin: "0 0 2px", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{l.address}</p>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>땅주인: {l.owner_name} · {l.owner_phone}</p>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: l.status === "pending" ? "rgba(212,168,83,0.15)" : l.status === "matched" ? "rgba(59,130,246,0.15)" : "rgba(16,185,129,0.15)", color: l.status === "pending" ? "#D4A853" : l.status === "matched" ? "#3B82F6" : "#10B981" }}>
                          {l.status === "pending" ? "대기중" : l.status === "matched" ? "연결됨" : l.status === "accepted" ? "수락됨" : "완료"}
                        </span>
                        <select
                          defaultValue={l.status}
                          onChange={async (e) => {
                            try {
                              await apiFetch(`/api/admin/owner-listings/${l.id}`, {
                                method: "PATCH",
                                body: JSON.stringify({ status: e.target.value }),
                              });
                              const qs = ownerStatusFilter ? `?status=${ownerStatusFilter}` : "";
                              const res = await apiFetch<{ listings: any[] }>(`/api/admin/owner-listings${qs}`);
                              setOwnerListings(res.listings || []);
                            } catch (err: any) {
                              alert(err.message);
                            }
                          }}
                          style={{ padding: "4px 8px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 12 }}
                        >
                          <option value="pending">대기중</option>
                          <option value="matched">연결됨</option>
                          <option value="accepted">수락됨</option>
                          <option value="closed">완료</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" as const }}>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>지목: <strong style={{ color: "var(--text-secondary)" }}>{l.land_type}</strong></span>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>면적: <strong style={{ color: "var(--text-secondary)" }}>{(l.area_sqm || 0).toLocaleString()}㎡</strong></span>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>희망가: <strong style={{ color: "#FBBF24" }}>{(l.asking_price || 0).toLocaleString()}만원</strong></span>
                      {l.matched_agent_name && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>담당 중개사: <strong style={{ color: "var(--text-primary)" }}>{l.matched_agent_name}</strong></span>}
                    </div>
                    {l.notes && <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)" }}>특이사항: {l.notes}</p>}
                    <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text-muted)" }}>등록일: {new Date(l.created_at).toLocaleDateString("ko-KR")}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "pending-verify" && (
          <>
            <h2 style={s.sectionTitle}>인증 대기 중개사</h2>
            {brokers.filter((b) => b.verification_status === "pending").length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: 14 }}>인증 대기 중인 중개사가 없습니다</p>
            ) : (
              <div style={s.brokerList}>
                {brokers.filter((b) => b.verification_status === "pending").map((b) => (
                  <div key={b.id} style={s.brokerCard}>
                    <div style={s.brokerTop}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <strong style={{ fontSize: 16 }}>{b.name}</strong>
                          <span style={{ padding: "1px 6px", borderRadius: 4, background: "rgba(245,158,11,0.15)", color: "var(--lt-orange)", fontSize: 11, fontWeight: 600 }}>{"심사중"}</span>
                        </div>
                        <p style={s.brokerSub}>{b.office_name}</p>
                        <p style={s.brokerSub}>{b.region_sido} {b.region_sigungu} | {b.license_number}</p>
                        {b.verification_requested_at && (
                          <p style={{ ...s.brokerSub, marginTop: 4 }}>{"신청일: "}{new Date(b.verification_requested_at).toLocaleDateString("ko-KR")}</p>
                        )}
                      </div>
                      <div style={{ textAlign: "right" as const }}>
                        <div style={{ ...s.trustCircle, borderColor: trustColor(b.trust_score), color: trustColor(b.trust_score) }}>
                          {b.trust_score}
                        </div>
                      </div>
                    </div>
                    <div style={s.actions}>
                      <ActionBtn
                        label="서류 보기"
                        color="var(--lt-blue)"
                        loading={false}
                        onClick={() => loadVerificationFiles(b.id, b.name)}
                      />
                      <ActionBtn
                        label="인증 승인"
                        color="var(--lt-green)"
                        loading={actionLoading === b.id + "verify"}
                        onClick={() => handleAction(b.id, "verify")}
                      />
                      <ActionBtn
                        label="인증 거절"
                        color="var(--lt-red)"
                        loading={actionLoading === b.id + "reject-verify"}
                        onClick={() => setRejectModal({ brokerId: b.id, name: b.name })}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* 신뢰도 조정 모달 */}
      {trustModal && (
        <div style={s.modalOverlay} onClick={() => setTrustModal(null)}>
          <div style={{ ...s.modal, padding: m ? 16 : 24, margin: m ? "0 12px" : 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", color: "var(--text-primary)" }}>
              신뢰도 조정: {trustModal.name}
            </h3>
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 12 }}>
              <div>
                <label style={s.modalLabel}>점수 변경 (예: +10, -20)</label>
                <input
                  type="number"
                  value={trustChange}
                  onChange={(e) => setTrustChange(e.target.value)}
                  placeholder="-30"
                  style={s.modalInput}
                />
              </div>
              <div>
                <label style={s.modalLabel}>사유</label>
                <input
                  value={trustReason}
                  onChange={(e) => setTrustReason(e.target.value)}
                  placeholder="NDA 위반, 허위 매물 등"
                  style={s.modalInput}
                />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button style={s.modalCancel} onClick={() => setTrustModal(null)}>
                  취소
                </button>
                <button
                  style={s.modalConfirm}
                  onClick={handleTrustSubmit}
                  disabled={!trustChange || !trustReason}
                >
                  적용
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 서류 보기 모달 */}
      {fileModal && (
        <div style={s.modalOverlay} onClick={() => setFileModal(null)}>
          <div style={{ ...s.modal, maxWidth: 560, padding: m ? 16 : 24 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", color: "var(--text-primary)", fontSize: 16 }}>
              {fileModal.name} — {"인증 서류"}
            </h3>
            {fileModalLoading ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center" as const, padding: "20px 0" }}>{"불러오는 중..."}</p>
            ) : fileModalFiles.length === 0 ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center" as const, padding: "20px 0" }}>{"제출된 서류가 없습니다"}</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: fileModalFiles.length === 1 ? "1fr" : "repeat(2, 1fr)", gap: 12 }}>
                {fileModalFiles.map((f, i) => (
                  <img key={i} src={f} alt={`서류 ${i + 1}`} style={{ width: "100%", borderRadius: "var(--radius-md)", border: "1px solid var(--border-light)" }} />
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button style={s.modalCancel} onClick={() => setFileModal(null)}>{"닫기"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 거절 사유 모달 */}
      {rejectModal && (
        <div style={s.modalOverlay} onClick={() => { setRejectModal(null); setRejectReason(""); }}>
          <div style={{ ...s.modal, padding: m ? 16 : 24, margin: m ? "0 12px" : 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", color: "var(--text-primary)" }}>
              {"인증 거절: "}{rejectModal.name}
            </h3>
            <div>
              <label style={s.modalLabel}>{"거절 사유"}</label>
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="서류 불충분, 자격 미달 등"
                style={s.modalInput}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button style={s.modalCancel} onClick={() => { setRejectModal(null); setRejectReason(""); }}>{"취소"}</button>
              <button
                style={{ ...s.modalConfirm, background: "var(--lt-red)", color: "#fff" }}
                onClick={handleRejectWithReason}
                disabled={!rejectReason}
              >
                {"거절"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div style={s.statCard}>
      <p style={{ ...s.statValue, color: color || "var(--text-primary)" }}>{value}</p>
      <p style={s.statLabel}>{label}</p>
    </div>
  );
}

function ActionBtn({
  label,
  color,
  onClick,
  loading,
}: {
  label: string;
  color: string;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <button
      style={{ ...s.actionBtn, borderColor: color, color }}
      onClick={onClick}
      disabled={loading}
    >
      {loading ? "..." : label}
    </button>
  );
}

const s: Record<string, React.CSSProperties> = {
  loading: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "var(--text-muted)" },
  container: { minHeight: "100vh", background: "var(--bg-primary)" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 24px", borderBottom: "1px solid var(--border-light)", background: "var(--bg-card)",
  },
  headerLogo: { fontSize: 22, fontWeight: 800, color: "var(--text-primary)", margin: 0 },
  adminBadge: {
    padding: "2px 8px", borderRadius: 4, background: "var(--lt-red)", color: "#fff",
    fontSize: 11, fontWeight: 700, letterSpacing: 1,
  },
  bulkBtn: {
    padding: "8px 16px", borderRadius: "var(--radius-md)", border: "none",
    background: "var(--lt-gold)", color: "#0F172A", fontSize: 13, fontWeight: 600,
    textDecoration: "none", display: "inline-flex", alignItems: "center",
  },
  backBtn: {
    padding: "8px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)",
    background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer",
  },
  tabs: {
    display: "flex", borderBottom: "1px solid var(--border-light)", background: "var(--bg-card)",
    padding: "0 24px",
  },
  tab: {
    padding: "12px 20px", border: "none", background: "transparent", color: "var(--text-muted)",
    fontSize: 14, fontWeight: 500, cursor: "pointer", borderBottom: "2px solid transparent",
  },
  tabActive: { color: "var(--lt-gold)", borderBottomColor: "var(--lt-gold)" },
  main: { maxWidth: 1200, margin: "0 auto", padding: "24px 32px" },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 16px" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 },
  statCard: {
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-lg)", padding: 16, textAlign: "center" as const,
  },
  statValue: { fontSize: 28, fontWeight: 700, margin: 0 },
  statLabel: { fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0" },
  brokerList: { display: "flex", flexDirection: "column" as const, gap: 12 },
  brokerCard: {
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-lg)", padding: 16,
  },
  brokerTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  brokerSub: { fontSize: 13, color: "var(--text-muted)", margin: "2px 0 0" },
  roleBadge: {
    padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.15)",
    color: "var(--lt-red)", fontSize: 11, fontWeight: 600,
  },
  suspendBadge: {
    padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.3)",
    color: "#fff", fontSize: 11, fontWeight: 600,
  },
  trustCircle: {
    width: 44, height: 44, borderRadius: "50%", border: "2px solid",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, fontWeight: 700,
  },
  actions: {
    display: "flex", flexWrap: "wrap" as const, gap: 6, marginTop: 12,
    paddingTop: 12, borderTop: "1px solid var(--border-light)",
  },
  actionBtn: {
    padding: "4px 10px", borderRadius: "var(--radius-sm)", border: "1px solid",
    background: "transparent", fontSize: 12, cursor: "pointer", fontWeight: 500,
  },
  modalOverlay: {
    position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
  },
  modal: {
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-xl)", padding: 24, width: "100%", maxWidth: 400,
  },
  modalLabel: { fontSize: 13, color: "var(--text-secondary)", display: "block", marginBottom: 4 },
  modalInput: {
    width: "100%", padding: "10px 12px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)", background: "var(--bg-secondary)",
    color: "var(--text-primary)", fontSize: 16, outline: "none",
  },
  modalCancel: {
    padding: "8px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)",
    background: "transparent", color: "var(--text-secondary)", cursor: "pointer",
  },
  modalConfirm: {
    padding: "8px 16px", borderRadius: "var(--radius-md)", border: "none",
    background: "var(--lt-gold)", color: "#0F172A", fontWeight: 600, cursor: "pointer",
  },
};
